import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { SikoAutoUnsubscribe } from '@siko/common';
import { SikoOrderConfirmationModule } from '@siko/features/checkout/order-confirmation/order-confirmation.module';
import { SikoCouponCardComponent } from '@siko/features/shared-components/coupon-card/coupon-card.component';
import { SikoOrderDetailsItemsModule } from '@siko/features/shared-components/order-details-items/order-details-items.module';
import { SikoOrderDetailTotalsModule } from '@siko/features/shared-components/order-details-total/order-detial-totals.module';
import { SikoOrder } from '@siko/models/order.model';
import { SikoB2bPromotionResult } from '@siko/models/product.model';
import { SikoB2BUser } from '@siko/models/user.model';
import { B2bCommonModule } from '@siko/shared/b2b-common.module';
import {
   isNotNullable,
   RoutingService,
   SiteContextSelectors,
   StateWithSiteContext,
   TranslationService,
} from '@spartacus/core';
import { OrderActions, OrderHistoryService } from '@spartacus/order/core';
import { SpinnerModule } from '@spartacus/storefront';
import { UserProfileFacade } from '@spartacus/user/profile/root';
import { Observable, Subject, Subscription } from 'rxjs';
import {distinctUntilChanged, filter, take, takeUntil, tap} from 'rxjs/operators';
import {ActiveCartFacade, MultiCartFacade} from "@spartacus/cart/base/root";

@SikoAutoUnsubscribe(['loadOrderFailSubscription', 'languageSubscription'])
@Component({
   changeDetection: ChangeDetectionStrategy.OnPush,
   selector: 'app-order-confirmation-outlet',
   templateUrl: './order-confirmation-outlet.component.html',
   providers: [OrderHistoryService],
   imports: [
      B2bCommonModule,
      SikoOrderDetailTotalsModule,
      SikoOrderConfirmationModule,
      SikoOrderDetailsItemsModule,
      SpinnerModule,
      SikoCouponCardComponent,
   ],
})
export class SikoOrderConfirmationOutletComponent implements OnInit {
   paymentGatewayPaymentStatus?: string | null;
   destroy$ = new Subject<void>();
   loadOrderFailSubscription?: Subscription;
   hasGiveawayCoupons = false;

   sikoUser$: Observable<SikoB2BUser> = this.userProfileFacade
      .get()
      .pipe(filter(isNotNullable), takeUntil(this.destroy$));

   order$: Observable<SikoOrder> = this.orderHistoryService.getOrderDetails().pipe(
      filter(isNotNullable),
      tap((order: SikoOrder) => {
         this.hasGiveawayCoupons = this.checkIfHasGiveawayCoupons(order);
      })
   );

   languageChange$: Observable<string | null> = this.siteContextStore.pipe(
      select(SiteContextSelectors.getActiveLanguage)
   );

   languageSubscription: Subscription = this.languageChange$.pipe(distinctUntilChanged()).subscribe(() => {
      const orderCode = this.activatedRoute.snapshot.paramMap.get('orderCode');
      if (orderCode) {
         this.orderHistoryService.loadOrderDetails(orderCode);
      }
   });

   constructor(
      private readonly siteContextStore: Store<StateWithSiteContext>,
      private readonly activeCartFacade: ActiveCartFacade,
      private readonly activatedRoute: ActivatedRoute,
      private readonly multiCartFacade: MultiCartFacade,
      private readonly routingService: RoutingService,
      private readonly userProfileFacade: UserProfileFacade,
      private readonly orderHistoryService: OrderHistoryService,
      private readonly actions: Actions,
      public translation: TranslationService
   ) {}

   ngOnInit(): void {
      this.loadOrderFailSubscription = this.actions.pipe(ofType(OrderActions.LOAD_ORDER_DETAILS_FAIL)).subscribe(() => {
         this.routingService.goByUrl('/');
      });

      this.removeCartFromState();
      this.paymentGatewayPaymentStatus = this.activatedRoute.snapshot.queryParamMap.get('status');
   }

   private checkIfHasGiveawayCoupons(sikoOrder: SikoOrder): boolean {
      return (
         sikoOrder.appliedOrderPromotions?.find(
            (i: SikoB2bPromotionResult) => i.giveAwayCouponCodes && i.giveAwayCouponCodes.length > 0
         ) !== undefined
      );
   }

   private removeCartFromState(): void {
      this.activeCartFacade.getActiveCartId().pipe(
          take(1),
          tap((cartId) => this.multiCartFacade.removeCart(cartId))
      ).subscribe();
   }
}
